import type { ReactElement } from 'react';
import React, { useMemo } from 'react';
import { find, omit } from 'lodash';
import type { Props as BaseProps } from 'feature-components/assessment-filters/base-filter';
import BaseFilter from 'feature-components/assessment-filters/base-filter';
import {
  transformEntryFilters,
  transformFilters
} from 'pages/clusters/helpers/aggregated-report.helpers';
import type { Filters, FormValues } from 'pages/clusters/types';
import type { BaseClusterCustomer } from 'services/assist-api/entities/customer-groups-customers/customer-groups-customers';
import { useClusterCustomers } from 'services/assist-api/entities/customer-groups-customers/customer-groups-customers';
import { useClusterFilterCustomers } from 'services/assist-api/entities/test-instance-response/filters/customer-group/customer';
import { isInitialLoad } from 'utils/check-query-status';

export type Props<IsMulti extends boolean> = {
  entryFilters: FormValues | undefined;
  filters: Filters | undefined;
  label?: BaseProps<BaseClusterCustomer, IsMulti>['label'];
} & Pick<
  BaseProps<BaseClusterCustomer, IsMulti>,
  'isMulti' | 'isClearable' | 'isSearchable' | 'value' | 'onChange' | 'isDisabled'
>;
/**
 * Filter that is auto populated by backend defined genders
 *
 *  - fetches gender
 *  - has loading state until genders are fetched
 *  - disables genders not included in `enabledIds`
 */
export default function OrganisationFilter<IsMulti extends boolean = false>({
  entryFilters,
  filters,
  label = 'Organisation',
  isClearable = true,
  isSearchable = true,
  ...props
}: Props<IsMulti>): ReactElement {
  /* Get all clusterCustomers that are linked to the current cluster */
  const clusterCustomers = useClusterCustomers({
    filters: { customerGroup: entryFilters?.cluster }
  });
  /* get customers that are relevant to the main data query (have data) */
  const enabledCustomers = useClusterFilterCustomers(
    { filters: transformEntryFilters(entryFilters) },
    { enabled: !!entryFilters }
  );
  /* get customers that are relevant to the already filtered data (have data) */
  const filteredCustomers = useClusterFilterCustomers(
    {
      filters: {
        ...transformEntryFilters(entryFilters),
        ...transformFilters(omit(filters, 'clusterCustomers'))
      }
    },
    { enabled: !!entryFilters }
  );
  /* get clusterCustomer ids that can contribute data to the built query */
  const filteredClusterCustomerIds = useMemo(
    () =>
      filteredCustomers.data?.json.map(
        ({ id }) =>
          clusterCustomers.data?.json.find(({ customer }) => customer.id === id)?.id as number
      ),
    [clusterCustomers.data?.json, filteredCustomers.data?.json]
  );

  return (
    <BaseFilter<BaseClusterCustomer, IsMulti>
      label={label}
      options={clusterCustomers.data?.json}
      getOptionValue={(o): string => String(o.id)}
      getOptionLabel={(o): string => o.customer.name}
      isOptionDisabled={({ customer }): boolean =>
        !!enabledCustomers.data?.json?.length &&
        !find(enabledCustomers.data?.json, { id: customer.id })
      }
      isLoading={isInitialLoad(clusterCustomers, enabledCustomers, filteredCustomers)}
      isClearable={isClearable}
      isSearchable={isSearchable}
      filteredIds={filteredClusterCustomerIds}
      {...props}
    />
  );
}
